import { useEffect, useState } from "react";
import { getZones, createZone, updateZone, deleteZone } from "interfaces/zone";
import ZoneForm from "components/zone/zoneForm";
import { getMyInfo } from "interfaces/user";
import { toast } from "react-toastify";


const ZoneList: React.FC = () => {
  const [zones, setZones] = useState<any[]>([]);
  const [editingZone, setEditingZone] = useState<any | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [user, setUser] = useState<any>(null);

  // Charger les zones
  const fetchZones = async () => {
    const res = await getZones();
    if (res) setZones(res.data);
  };

  const initUser = async () => {
    const res = await getMyInfo();
    if (res) setUser(res.data);
  }

  useEffect(() => {
    fetchZones();
    initUser();
  }, []);

  // Sauvegarde depuis ZoneForm (création ou édition)
  const handleSave = async (zone: any) => {

    if (user?.isParent === false) {
      toast.error("Seul le compte principal peut modifier cette configuration.");
      return;
    }

    if (zone._id) {
      await updateZone(zone._id, { name: zone.name });
    } else {
      await createZone({ name: zone.name });
    }
    setShowForm(false);
    setEditingZone(null);
    fetchZones();
  };

  const handleDelete = async (id: string) => {

    if (user?.isParent === false) {
      toast.error("Seul le compte principal peut modifier cette configuration.");
      return;
    }

    await deleteZone(id);
    fetchZones();
  };

  return (
    <div className="">
      {/* Header */}
      <div className="bg-white border rounded-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-gray-900 font-semibold text-2xl mb-2">Zones de stock</h1>
              <p className="text-gray-600 text-sm">Gérez les zones de rangement de votre stock</p>
            </div>
            <button
              className="bg-toola-500 text-white rounded-lg px-4 py-2 hover:bg-opacity-90 transition-colors flex items-center gap-2"
              onClick={() => {
                setEditingZone(null);
                setShowForm(true);
              }}
            >
              <span className="text-lg">+</span>
              Ajouter une zone
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {zones.length === 0 ? (
          // État vide
          <div className="bg-white border-gray-100 rounded-lg shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl text-gray-400">#</span>
            </div>
            <h3 className="text-gray-900 font-semibold text-lg mb-2">Aucune zone créée</h3>
            <p className="text-gray-600 text-sm mb-6">Commencez par créer votre première zone de stockage</p>
            <button
              className="bg-toola-500 text-white rounded-lg px-4 py-2 hover:bg-opacity-90 transition-colors"
              onClick={() => {
                setEditingZone(null);
                setShowForm(true);
              }}
            >
              Créer ma première zone
            </button>
          </div>
        ) : (
          // Liste des zones
          <div className="bg-white border-gray-100 rounded-lg shadow-sm overflow-hidden">
            {/* En-tête du tableau */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-9">
                  <span className="text-gray-600 text-sm font-medium">Nom de la zone</span>
                </div>
                <div className="col-span-3 text-right">
                  <span className="text-gray-600 text-sm font-medium">Actions</span>
                </div>
              </div>
            </div>

            {/* Lignes du tableau */}
            <div className="divide-y divide-gray-100">
              {zones.map((zone, index) => (
                <div
                  key={zone._id}
                  className={`px-6 py-4 hover:bg-gray-50 transition-colors ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                  }`}
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Nom de la zone */}
                    <div className="col-span-9">
                      <span className="text-gray-900 font-medium">{zone.name}</span>
                    </div>

                    {/* Actions */}
                    <div className="col-span-3 flex items-center justify-end gap-2">
                      <button
                        className="border-toola-500 text-toola-500 border rounded-lg px-3 py-1.5 text-sm hover:bg-toola-500 hover:bg-opacity-5 transition-colors"
                        onClick={() => {
                          setEditingZone(zone);
                          setShowForm(true);
                        }}
                      >
                        Modifier
                      </button>
                      <button
                        className="bg-red-400 text-white rounded-lg px-3 py-1.5 text-sm hover:bg-red-500 transition-colors"
                        onClick={() => handleDelete(zone._id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer avec statistiques */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-sm">
                  {zones.length} zone{zones.length > 1 ? 's' : ''} au total
                </span>
                <button
                  className="text-toola-500 text-sm hover:underline transition-colors"
                  onClick={() => {
                    setEditingZone(null);
                    setShowForm(true);
                  }}
                >
                  + Ajouter une autre zone
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Formulaire (création ou édition) */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            <ZoneForm
              initialZone={editingZone}
              onSave={handleSave}
              onCancel={() => {
                setShowForm(false);
                setEditingZone(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

};

export default ZoneList;
